import axiosClient from './axiosClient';

/**
 * Typed axios wrappers for `/api/admin/catalogs/:catalog`.
 *
 * Round 2 exposes `task-types` and `task-categories`; Round 4 adds
 * `task-reward-types` and `wallet-types`. Other catalogs mount via the
 * same shell — add their slugs to `CatalogSlug` as they land.
 */

export type CatalogSlug =
  | 'task-types'
  | 'task-categories'
  | 'task-reward-types'
  | 'wallet-types'
  | 'notification-channels'
  | 'notification-categories'
  | 'component-types'
  | 'onboarding-components'
  | 'canonical-fields'
  | 'cohort-attribute-defs';

export interface CatalogRow {
  code: string;
  name: string;
  description: string | null;
  active: boolean;
  /**
   * JSONB schema blob — present for catalogs that describe structured
   * payloads (component-types, onboarding-components). Undefined/null for
   * simple reference catalogs (task-types, etc). Rendered via Monaco in
   * the side drawer when non-null.
   */
  schema_json?: object | null;
  /**
   * Per-catalog scalar extension columns. Round 6 ships these for the two
   * domain catalogs: canonical-fields (`data_type`) and
   * cohort-attribute-defs (`data_type` + `value_source`). Older catalogs
   * simply don't populate them; CatalogShell inline-edit is driven by the
   * `extraColumns` prop.
   */
  data_type?: string | null;
  value_source?: string | null;
  created_at: string;
  created_by: string | null;
  updated_at: string | null;
  updated_by: string | null;
}

export interface ListCatalogResponse {
  rows: CatalogRow[];
}

export interface ListCatalogParams {
  q?: string;
  sort?: 'name' | 'code' | 'updated_at';
  dir?: 'asc' | 'desc';
}

export interface CreateCatalogRowPayload {
  code: string;
  name: string;
  description?: string | null;
}

export interface UpdateCatalogRowPayload {
  name?: string;
  description?: string | null;
  active?: boolean;
  schema_json?: object | null;
  data_type?: string | null;
  value_source?: string | null;
}

export interface CreateCatalogRowPayloadWithSchema extends CreateCatalogRowPayload {
  schema_json?: object | null;
}

const base = (slug: CatalogSlug) => `/api/admin/catalogs/${slug}`;

export const adminCatalogApi = {
  list: async (slug: CatalogSlug, params: ListCatalogParams = {}): Promise<CatalogRow[]> => {
    const res = await axiosClient.get<ListCatalogResponse>(base(slug), { params });
    return res.data.rows;
  },

  create: async (slug: CatalogSlug, payload: CreateCatalogRowPayload): Promise<CatalogRow> => {
    const res = await axiosClient.post<CatalogRow>(base(slug), payload);
    return res.data;
  },

  update: async (
    slug: CatalogSlug,
    code: string,
    payload: UpdateCatalogRowPayload,
  ): Promise<CatalogRow> => {
    const res = await axiosClient.patch<CatalogRow>(
      `${base(slug)}/${encodeURIComponent(code)}`,
      payload,
    );
    return res.data;
  },

  remove: async (slug: CatalogSlug, code: string): Promise<void> => {
    await axiosClient.delete(`${base(slug)}/${encodeURIComponent(code)}`);
  },
};
